(ns practitest.frontend.components2.dashboard.charts.marks
  (:require [practitest.frontend.components2.dashboard.charts.marks-gen :as gen]
            [practitest.frontend.components2.dashboard.charts.marks-bar :as bar]
            [practitest.frontend.components2.dashboard.charts.markers :as markers]
            ["d3" :as d3]))

(defmulti render!
  (fn [_svg-g mark _spec] (:type mark)))

(defn- grouping-field [encode]
  (or (get-in encode [:color :field])
      (get-in encode [:detail :field])))

(defn- sorting-field [encode]
  (or (get-in encode [:order :field])
      (get-in encode [:x :field])))

(def AreaRows
  [:vector [:map-of :string [:or inst? number?]]])

(defn prepare-area-rows
  {:malli/schema [:=> [:cat
                       [:sequential inst?]
                       [:map-of inst? [:vector [:map
                                                [:date  inst?]
                                                [:group :string]
                                                [:value number?]]]]
                       :keyword]
                  AreaRows]}
  [categories by-category x-field]
  (mapv (fn [cat]
          (let [rows-for-cat (get by-category cat [])
                base-row     {(name x-field) cat}]
            (reduce (fn [obj {:keys [group value]}]
                      (assoc obj (str group) value))
                    base-row
                    rows-for-cat)))
        categories))

(defn- prepare-data [encode data]
  (let [grp-field (grouping-field encode)
        srt-field (sorting-field encode)]
    (-> data
        (cond->>
            (vector? data) (group-by grp-field))
        (update-vals #(vec (sort-by srt-field %))))))

(defn- line-layer
  "Builds a plain JS array of per-category values for one line series, so it
   can be walked by index the same way markers/render! walks stacked layers."
  [cat-index x-field value-field {:keys [key points]}]
  (let [values (reduce (fn [acc pt]
                          (if-let [i (get cat-index (.getTime (get pt x-field)))]
                            (assoc acc i (get pt value-field))
                            acc))
                        (vec (repeat (count cat-index) nil))
                        points)
        arr    (clj->js values)]
    (set! (.-key arr) key)
    arr))

(defn- line-animation-defaults [mark]
  (merge {:ease-exit     (.-easeCubicIn d3)
          :ease-update   (.-easeCubicInOut d3)
          :ease-draw     (.-easeCubicOut d3)
          :exit-duration   200
          :update-duration 300
          :draw-duration   1200}
         (:animation mark)))

(defn- trace-in!
  "Reveals a freshly-entered line path by animating stroke-dashoffset from
   the path's full length down to 0, so it appears to draw itself in."
  [sel duration ease]
  (-> sel
      (.attr "stroke-dasharray" (fn [_d] (this-as this (.getTotalLength this))))
      (.attr "stroke-dashoffset" (fn [_d] (this-as this (.getTotalLength this))))
      (.transition "line-trace")
      (.duration duration)
      (.ease ease)
      (.attr "stroke-dashoffset" 0)
      (.on "end" (fn [_d] (this-as this (.setAttribute this "stroke-dasharray" "none"))))))

(defmethod render! :line
  [svg mark {:keys [data encode width height tooltip point tooltip-spec] :as ctx}]
  (let [marks-g     (.select svg "g.chart g.marks")
        color-scale (get-in encode [:color :d3-scale])
        x-scale     (get-in encode [:x :d3-scale])
        y-scale     (get-in encode [:y :d3-scale])
        x-type      (get-in encode [:x :type])
        x-field     (sorting-field encode)
        value-field (get-in encode [:y :field])
        line-gen    (gen/line ctx)
        {:keys [ease-exit ease-update ease-draw
                exit-duration update-duration draw-duration]} (line-animation-defaults mark)
        series   (->> data
                      (prepare-data encode)
                      (mapv (fn [[k line-data]]
                              {:key    (some-> k name)
                               :color  (when color-scale (color-scale k))
                               :points line-data})))
        paths    (-> marks-g
                     (.selectAll "path.mark-line")
                     (.data (clj->js series) (fn [d] (aget d "key"))))
        entered  (-> (.enter paths)
                     (.append "path")
                     (.attr "class" (fn [d] (str "mark-line " (aget d "key"))))
                     (.attr "fill" "none")
                     (.attr "stroke-width" 2)
                     (.attr "stroke" (fn [d] (or (aget d "color") "#888")))
                     (.attr "d" (fn [d] (line-gen (aget d "points")))))]
    (trace-in! entered draw-duration ease-draw)
    (-> paths
        (.attr "stroke" (fn [d] (or (aget d "color") "#888")))
        (.transition "line-update")
        (.duration update-duration)
        (.ease ease-update)
        (.attr "d" (fn [d] (line-gen (aget d "points")))))
    (-> (.exit paths)
        (.transition)
        (.duration exit-duration)
        (.ease ease-exit)
        (.style "opacity" 0)
        (.remove))
    (when point
      (let [categories (or (get-in encode [:x :scale :domain])
                            (->> data (map x-field) (sort-by #(.getTime %)) (dedupe)))
            cat-index  (into {} (map-indexed (fn [i cat] [(.getTime cat) i]) categories))
            layers     (into-array (map (partial line-layer cat-index x-field value-field) series))]
        (markers/render!
         {:marks-g      marks-g
          :x-scale      x-scale
          :x-type       x-type
          :x-format     (get-in encode [:x :axis :format])
          :y-fn         (fn [^js layer cat-idx]
                          (y-scale (or (aget layer cat-idx) 0)))
          :value-fn     (fn [^js layer cat-idx]
                          (aget layer cat-idx))
          :color-fn     (fn [^js layer]
                          (color-scale (.-key layer)))
          :categories   categories
          :layers       layers
          :inner-width  width
          :baseline-y   height
          :tooltip      tooltip
          :tooltip-spec tooltip-spec
          :show-zero?   true})))))

(defmethod render! :bar
  [svg mark {:keys [encode] :as ctx}]
  (if (get-in encode [:y :stacked])
    (bar/render-stacked! svg mark ctx)
    (bar/render-bar! svg mark ctx)))

(defmethod render! :area
  [svg _mark {:keys [data encode width tooltip point tooltip-spec]}]
  (let [marks-g       (.select svg "g.chart g.marks")
        x-scale       (get-in encode [:x :d3-scale])
        y-scale       (get-in encode [:y :d3-scale])
        color-scale   (get-in encode [:color :d3-scale])
        x-type        (get-in encode [:x :type])
        x-field       (get-in encode [:x :field])
        categories    (or (get-in encode [:x :scale :domain])
                          (->> data (map x-field) (sort-by #(.getTime %)) (dedupe)))
        series-field  (get-in encode [:color :field])
        series-labels (->> data (map series-field) distinct)
        baseline-y    (y-scale 0)
        by-category   (group-by x-field data)
        row-data      (clj->js (prepare-area-rows categories by-category x-field))
        stack         (-> (.stack d3) (.keys (into-array (map str series-labels))))
        layers        (stack row-data)
        series-sel    (-> marks-g
                          (.selectAll "path.mark-area")
                          (.data layers (fn [^js layer] (.-key layer))))
        entered       (-> (.enter series-sel)
                          (.append "path")
                          (.attr "class" "mark-area"))
        x-mid         (fn [^js d] (x-scale (aget (.-data d) (name x-field))))
        area-gen      (gen/area x-mid y-scale)
        area-flat     (gen/area-flat x-mid baseline-y)]

    (-> (.exit series-sel)
        (.transition) (.duration 200)
        (.style "opacity" 0)
        (.remove))

    (-> entered
        (.attr "fill" (fn [^js layer] (color-scale (.-key layer))))
        (.attr "fill-opacity" 0.8)
        (.attr "d" (fn [^js layer] (area-flat layer))))

    (-> (.merge entered series-sel)
        (.attr "fill" (fn [^js layer] (color-scale (.-key layer))))
        (.attr "fill-opacity" 0.8)
        (.transition) (.duration 300) (.ease (.-easeCubicOut d3))
        (.attr "d" (fn [^js layer] (area-gen layer))))

    (when point
      (markers/render!
       {:marks-g      marks-g
        :x-scale      x-scale
        :x-type       x-type
        :x-format     (get-in encode [:x :axis :format])
        :y-fn         (fn [^js layer cat-idx]
                        (y-scale (aget (aget layer cat-idx) 1)))
        :value-fn     (fn [^js layer cat-idx]
                        (let [pt (aget layer cat-idx)]
                          (- (aget pt 1) (aget pt 0))))
        :color-fn     (fn [^js layer]
                        (color-scale (.-key layer)))
        :categories   categories
        :layers       layers
        :inner-width  width
        :baseline-y   baseline-y
        :tooltip      tooltip
        :tooltip-spec tooltip-spec}))))
